export class StallError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StallError";
  }
}

/**
 * Once-per-episode bookkeeping for a consumer of {@link withStallGuard}.
 *
 * An episode opens when a stall is reported and closes ONLY when the
 * consumer's progress version moves past where it stood at report time —
 * evaluated lazily, because the version can advance on a path the consumer
 * never sees succeed (e.g. orphaned work recovering late on its own).
 *
 * While an episode is open, {@link windowFor} returns a much shorter probe
 * window: a truly-poisoned operation would otherwise pay the FULL window per
 * retry — and with retries arriving faster than that, they pile up and
 * starve everything queued behind them. A short probe still detects genuine
 * recovery (a recovered operation advances the version almost immediately)
 * while capping how long any one retry can occupy the caller.
 */
export class StallEpisodeTracker {
  private reported = false;
  private versionAtReport = 0;
  private readonly probeWindowMs: number;

  constructor(private readonly windowMs: number) {
    this.probeWindowMs = Math.max(10, Math.round(windowMs / 10));
  }

  /** The guard window for the next operation: probe while an episode is open. */
  windowFor(currentVersion: number): number {
    return this.isOpen(currentVersion) ? this.probeWindowMs : this.windowMs;
  }

  /** Open while the latch is set AND the version has not moved past report. */
  isOpen(currentVersion: number): boolean {
    if (this.reported && currentVersion > this.versionAtReport) {
      this.reported = false;
    }
    return this.reported;
  }

  /**
   * Record a stall. Returns true when this opens a NEW episode (the caller
   * should report it once); false when it belongs to the one already open.
   */
  open(currentVersion: number): boolean {
    if (this.isOpen(currentVersion)) {
      return false;
    }
    this.reported = true;
    this.versionAtReport = currentVersion;
    return true;
  }
}

/**
 * Watchdog a promise for PROGRESS, not duration. The guard checks
 * `hasProgressed()` every `windowMs`: as long as each window sees progress,
 * a legitimately slow operation runs as long as it needs. Only a window with
 * zero progress rejects with a {@link StallError}.
 *
 * The underlying work cannot be cancelled (a stuck IndexedDB request or a
 * black-holed fetch has no abort path from here) — after a stall the promise
 * keeps running orphaned. A no-op catch is attached so a late rejection from
 * the orphan never surfaces as an unhandled rejection.
 */
export function withStallGuard<T>(
  promise: Promise<T>,
  {
    windowMs,
    hasProgressed,
    label,
  }: {
    windowMs: number;
    /** Called once per window; true resets the watchdog. */
    hasProgressed: () => boolean;
    label: string;
  },
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stall = new Promise<never>((_, reject) => {
    const check = () => {
      if (hasProgressed()) {
        timer = setTimeout(check, windowMs);
        return;
      }
      reject(new StallError(`${label} made no progress for ${windowMs}ms`));
    };
    timer = setTimeout(check, windowMs);
  });
  promise.catch(() => {});
  return Promise.race([promise, stall]).finally(() => {
    if (timer != null) clearTimeout(timer);
  });
}
